/**
 * EnergyDetail -- Extracted from DetailPage.jsx (A12 Sprint 2).
 *
 * Shows energy installations (PV, biogas, wind) with DB summary + substrate balance.
 * Smallest-first extraction per Cross-Block Pattern 4.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';

export default function EnergyDetail({ token, finance }) {
  const { t } = useTranslation();
  const [installations, setInstallations] = useState(null);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/farm/energy')
      .then(d => setInstallations(d))
      .catch(() => {});
  }, [token]);

  const energyDb = finance?.energy;
  const substrate = finance?.biogas_substrate;

  if (!installations?.installations?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-4xl mb-4">{'\u26A1'}</div>
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">{t('dashboard.energy')}</h3>
        <p className="text-xs text-[var(--aif-text-muted)] max-w-[250px]">{t('energy.noInstallations')}</p>
      </div>
    );
  }

  const currency = energyDb?.currency || 'EUR';

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="text-center py-3">
        <div className="text-2xl font-bold text-[var(--aif-color-primary)]">{installations.total_capacity_kw} kW</div>
        <div className="text-xs text-[var(--aif-text-muted)]">{installations.count} {t('energy.installations')}</div>
      </div>

      {/* Energy DB */}
      {energyDb?.per_installation?.length > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-2">{t('finance.dbI')}</h3>
          {energyDb.per_installation.map((inst, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-sm border-b border-[var(--aif-border-default)] last:border-0">
              <div>
                <span className="text-[var(--aif-text-primary)]">{inst.type_name}</span>
                <span className="text-xs text-[var(--aif-text-muted)] ml-2">{inst.capacity_kw} kW</span>
              </div>
              <span className="font-bold text-[var(--aif-color-primary)]">{inst.db_i?.toLocaleString()} {currency}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 text-sm font-bold">
            <span>{t('finance.total')}</span>
            <span className="text-[var(--aif-color-primary)]">{energyDb.branch_total?.db_i?.toLocaleString()} {currency}</span>
          </div>
        </div>
      )}

      {/* Biogas substrate balance */}
      {substrate?.has_biogas && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-2">{t('energy.substrate')}</h3>
          {substrate.substrate_demand?.corn_silage_t > 0 && (
            <div className="text-xs text-[var(--aif-text-muted)] mb-1">
              {t('energy.cornSilage')}: {substrate.substrate_demand.corn_silage_t} t {t('energy.demand')} | {substrate.own_production?.corn_silage_t || 0} t {t('energy.ownProd')}
              {substrate.corn_self_sufficient
                ? <span className="text-green-400 ml-1">{'\u2705'}</span>
                : <span className="text-orange-400 ml-1">{'\u26A0\uFE0F'} {t('energy.purchaseNeeded')}</span>}
            </div>
          )}
          {substrate.digestate_n_kg > 0 && (
            <div className="text-xs text-[var(--aif-text-muted)]">
              {t('energy.digestate')}: {substrate.digestate_m3?.toLocaleString()} m{'\u00B3'} | {substrate.digestate_n_kg?.toLocaleString()} kg N
            </div>
          )}
        </div>
      )}

      {/* Installation list */}
      <div className="space-y-2">
        {installations.installations.map((inst, i) => (
          <div key={i} className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--aif-text-primary)]">{inst.name || inst.type_name}</span>
              <span className="text-sm font-bold text-[var(--aif-text-primary)]">{inst.capacity_kw} kW</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-[var(--aif-text-muted)]">
              <span>{inst.type_name}</span>
              {inst.feed_in_tariff_ct_kwh && <span>| {inst.feed_in_tariff_ct_kwh} ct/kWh</span>}
              {inst.self_consumption_pct > 0 && <span>| {inst.self_consumption_pct}% {t('energy.selfConsumption')}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
